/**
 * Authentication context and hooks.
 * Provides user state and role-based access control for the frontend.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getStoredUser,
  getStoredTenant,
  getStoredTokens,
  login as apiLogin,
  logout as apiLogout,
  clearAuth,
} from './api';

export type UserRole = 'operador_caixa' | 'gerente' | 'administrador' | 'super_admin';

interface User {
  id: string;
  name: string;
  role: UserRole;
}

interface Tenant {
  id: string;
  name: string;
}

interface AuthContextValue {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (loginName: string, password: string, tenantId?: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [tenant, setTenant] = React.useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  // Initialize from localStorage
  React.useEffect(() => {
    const storedUser = getStoredUser();
    const storedTenant = getStoredTenant();
    const tokens = getStoredTokens();

    if (storedUser && storedTenant && tokens?.accessToken) {
      setUser(storedUser as User);
      setTenant(storedTenant);
    }
    setIsLoading(false);
  }, []);

  const login = React.useCallback(async (loginName: string, password: string, tenantId?: string) => {
    const data = await apiLogin(loginName, password, tenantId);
    setUser(data.user as User);
    setTenant(data.tenant);
  }, []);

  const logout = React.useCallback(async () => {
    await apiLogout();
    setUser(null);
    setTenant(null);
  }, []);

  const hasRole = React.useCallback(
    (...roles: UserRole[]) => {
      if (!user) return false;
      // Role hierarchy: administrador > gerente > operador_caixa
      const hierarchy: Record<UserRole, number> = {
        operador_caixa: 1,
        gerente: 2,
        administrador: 3,
        super_admin: 4,
      };
      const userLevel = hierarchy[user.role];
      return roles.some((role) => userLevel >= hierarchy[role]);
    },
    [user],
  );

  const value = React.useMemo(
    () => ({
      user,
      tenant,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      hasRole,
    }),
    [user, tenant, isLoading, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook that redirects to login if not authenticated,
 * or shows a toast and redirects if the user doesn't have the required role.
 */
export function useRequireAuth(...requiredRoles: UserRole[]) {
  const { isAuthenticated, isLoading, hasRole, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (requiredRoles.length > 0 && !hasRole(...requiredRoles)) {
      toast.error('Acesso negado. Você não tem permissão para acessar esta funcionalidade.');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, hasRole, navigate, user]);

  return { isAuthenticated, isLoading, hasRole };
}

/**
 * Route guard component that wraps pages requiring specific roles.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: UserRole[];
  children: React.ReactNode;
}) {
  const { isLoading } = useRequireAuth(...roles);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
